/**
 * The lobby avatar: a disc in the account bar showing one of the summoners
 * the account may use, and the picker that chooses it.
 *
 * The owner's idea ("pretty sure of this"): the profile picture is one of the
 * player's summoners — the official ones or the ones they generated in Moon
 * Dream — which makes generating summoners worth something on the hub too.
 *
 * Contract:
 *   - `mount(profile)` paints the disc from `profile.avatarPortraitUrl` (or the
 *     generic moon when none) and binds the button once. The disc has its
 *     box from the first paint (index.html + hub.css); only the picture
 *     swaps, so the account strip never moves (docs/asset-loading.md);
 *   - the button opens a dialog listing the summoners the account may use,
 *     read through the same `/api/deck-builder/characters-page` route the
 *     deck picker reads, with the same auth header. A page is AS MANY FACES
 *     AS FIT the dialog at this size — measured from the laid-out grid, not a
 *     constant — so the grid never scrolls and no row of faces is ever cut in
 *     half; Prev/Next own the rest (the route's cursor; a page seen once is
 *     kept for the way back), and a collection that fits on one page shows no
 *     pager. The route pages by ROW and a row without a portrait cannot be a
 *     face (the server refuses it), so the dialog reads past those rather than
 *     leaving holes in the grid;
 *   - THE DIALOG OPENS WHOLE. It is built unseen (ui/surface-entry-hold.js):
 *     laid out, so the page is measured for real, its names fitted in the
 *     real type, its pager final and every face on the page sampled and
 *     painted, then presented in ONE step. Until then its backdrop answers the
 *     press (and a tap on it closes the dialog); a wait past the shared 450 ms
 *     grace adds the ONE loading state, a spinner on that backdrop. The hold
 *     is capped 1.5 s after the reads answer. Only the faces that fit are
 *     downloaded;
 *   - a page turn keeps the page on screen until the next one is whole (its
 *     faces painted unseen), then swaps it in one step; past the grace the
 *     page it replaces dims. A page seen once comes back as it was. Once a
 *     page's faces are in, the next page's are fetched — ONE page ahead,
 *     dropped when the player moves away or closes;
 *   - the faces are the account's summoners, read ONCE for this dialog and
 *     the summoner select together (summoner-roster.js: the whole list, up
 *     to the route's cap for one read). The lobby warms them at idle, behind
 *     the summoner select (`warmForLobby`, lobby.js): the faces of the first
 *     two pages sampled one at a time in idle slots, each portrait decoded
 *     off the main thread first — a small set, read whole (owner, 28
 *     September 2026: "you could load like at least 20" summoners). Pointing
 *     at, focusing or pressing the disc warms the first page too, for an open
 *     that beats the lobby's warm;
 *   - every face carries its summoner's NAME under it, up to two balanced
 *     lines in the hub's serif; the tile is the face and its name, so a hover
 *     lights the CIRCLE rather than a rectangle around it;
 *   - choosing one calls `users.setAvatar` — the SERVER checks ownership — and
 *     repaints the disc from the server's answer, never from the click;
 *   - Escape, the backdrop and the × close; focus returns to the disc;
 *   - a refused choice is said aloud in a toast; nothing is painted;
 *   - there is no blank option: an account that has not chosen shows the
 *     default the server picks for it (the profile's `avatarIsDefault`), so
 *     the disc always has a face; the moon shows only until the picture is in;
 *   - the picture goes through the game's raster sampler
 *     (`MoonfallRasterSampler.enhanceImage`): a card portrait reduced to a
 *     ~60px disc in one compositor step aliases, and the stepped high-quality
 *     downscale is the same cure the card art uses;
 *   - the face painted last is remembered per account (with the size it was
 *     reduced for and the viewport it was measured in), and `warm(uid)` starts
 *     that same raster at the next sign-in, before the account read names the
 *     face — the boot veil waits for the disc, so its face must not start
 *     last. In another viewport only the bytes start (the disc's size follows
 *     the viewport); a face the account no longer names is dropped; a face
 *     remembered over 30 days ago is not started. `forget()` on sign-out,
 *     a refused login and erasure; a face that finishes painting after a
 *     forget, or under another account, is not remembered.
 */

import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
import { Firebase } from '../firebase-loader.js';
import { uiPx } from './ui-scale.js';
import { SurfaceEntryHold } from './surface-entry-hold.js';
import { TouchPress } from './touch-press.js';
import { SummonerRoster } from './summoner-roster.js';

let AvatarPicker = {};

AvatarPicker = (function () {
    const DIALOG_ID = 'avatar-picker';
    /* The most faces one page will ever hold. A page is really "as many as
       fit" (measured at paint), and this is the ceiling that keeps the fill
       loop and the route reads bounded on a very large screen. */
    const MAX_PAGE = 48;
    /* The raster a face is sampled at: the tile's 72px disc drawn at 116%
       (hub.css), the size the picker has always asked for. ONE request for
       the tile, the warm and every page, so all of them name the same
       sampled raster — the same pixels, never a smaller one. */
    const TILE_RASTER_PX = 84;
    /* A dialog held for its faces is shown at the latest this long after its
       reads answer (docs/asset-loading.md: every hold is capped). */
    const OPEN_REVEAL_CAP_MS = 1500;
    /* A page turn keeps the old page up at most this long for the new one. */
    const PAGE_TURN_CAP_MS = 1500;
    /* The shared art grace: a wait inside it shows nothing loading. */
    const LOADING_GRACE_MS = SurfaceEntryHold.LOADING_GRACE_MS;
    let button = null;
    let image = null;
    let dialog = null;
    let bound = false;
    let currentCharacterId = null;
    let openGeneration = 0;
    /* THE FACES OUTLIVE THE DIALOG. Every face read, in the route's order,
       for one account and language: a re-open paints them in its first frame
       and re-reads the first route page behind them, instead of reading
       everything again under a "Please wait" line. */
    /** @type {{ key: string, faces: Array<{ characterId: string, name: string, imageUrl: string }>, cursor: string | null, done: boolean, pending?: Promise<void> | null, reading?: Promise<void> | null } | null} */
    let facesRead = null;
    /* Settles once the disc shows its final face (the portrait decoded and
       painted, or no portrait to paint). The boot veil waits on it so the
       lobby never swaps the moon for a face in front of the player. */
    let painted = Promise.resolve();
    /* WHICH PAINT DECIDES THE DISC. Every paint that starts (a first face, a
       replacing face, the moon) takes the next number, and a paint acts —
       shows its picture, hides the disc, remembers its face — only while its
       number is the latest. Checked by URL instead, a paint abandoned for the
       moon came back to life when the same face was named again: its wait
       for the raster's load was still on the picture, the new download
       answered it, and the disc showed before its raster was in (review, 26
       September 2026). */
    let paintNumber = 0;
    /* Hands the wait of a replaced paint on to the paint that replaced it
       (`track`). */
    /** @type {((next: Promise<unknown>) => void) | null} */
    let handOver = null;
    /* THE LAST FACE, REMEMBERED FOR THE NEXT BOOT. The boot veil holds the
       lobby for this disc's face, and the face used to be asked for only once
       the account read had named it: on the meter's round 13 (25 September
       2026) it was the LAST thing the veil waited for on every WebKit boot and
       every cold one — an 835 KB portrait that WebKit downloads again on every
       visit (1.2-1.8 s on the 4G seat), then a reduction of ~0.2 s on a
       desktop and ~0.6 s at 4x CPU. The face painted last is kept here, per
       account, with the size it was reduced for, so the next boot starts it
       with the sign-in (`warm`, called from main.js). */
    const REMEMBERED_FACE_KEY = 'moonfall_avatar_face';
    /* A face remembered longer ago than this is not started. The warm is a
       guess, and an old guess is likelier wrong — every default face moves
       when a public summoner is added — while a wrong one costs a download
       and a reduction beside the sign-in's own round trips. */
    const REMEMBERED_FACE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
    /* The boot warm in flight: the face it loads and its picture. The picture
       stays referenced until the disc has painted, so the copy it loaded is
       still there when the disc asks; `drop()` ends a warm whose face the
       account no longer shows. */
    /** @type {{ url: string, picture: HTMLImageElement | null, settled: boolean, dropped: boolean, drop: () => void, result: Promise<boolean> | null } | null} */
    let warming = null;
    /* Counts the forgets. Sign-out and erasure forget the face and THEN await
       the server with the page still up and the account still signed in, and
       a face that finished painting in that wait wrote the record straight
       back, for an account just signed out or erased (review, 26 September
       2026). A paint remembers its face only if nothing was forgotten since
       it started, and only for the account it started under. */
    let forgets = 0;

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        const text = translated && translated !== key ? translated : fallback;
        return Object.entries(replacements).reduce(
            (out, [name, value]) => out.replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), String(value)),
            text,
        );
    }

    function resolve() {
        button = document.getElementById('user-avatar');
        image = document.getElementById('user-avatar-image');
        return Boolean(button && image);
    }

    /** What the disc asks the sampler for. ONE shape, shared by the paint and
        the boot warm, so both name the same sampled raster (one cache entry). */
    function discRequest(width, height) {
        return {
            displayWidth: width,
            displayHeight: height,
            fit: 'cover',
            positionX: 0.5,
            positionY: 0.2,
        };
    }

    function accountId() {
        try { return String(Firebase?.getUser?.()?.uid || ''); } catch (_) { return ''; }
    }

    /** The viewport's CSS size. It decides the disc's size (the phone page
        box, the UI scale above full HD), so a face reduced in one viewport is
        the right raster only in the same one. */
    function currentView() {
        return { width: Number(window.innerWidth) || 0, height: Number(window.innerHeight) || 0 };
    }

    /** What a paint needs to remember its face when it finishes: the
        account it started under and the forgets so far. */
    function rememberTicket() {
        return { uid: accountId(), forgets };
    }

    function rememberFace(url, size, view, ticket) {
        const uid = accountId();
        if (!uid || uid !== ticket.uid || ticket.forgets !== forgets) return;
        try {
            localStorage.setItem(REMEMBERED_FACE_KEY, JSON.stringify({
                uid, url, size, width: view.width, height: view.height, at: Date.now(),
            }));
        } catch (_) { /* private mode: the next boot simply loads as before */ }
    }

    function forgetFace() {
        forgets += 1;
        if (warming) warming.drop();
        try { localStorage.removeItem(REMEMBERED_FACE_KEY); } catch (_) { /* private mode */ }
        clearDiscStore();
    }

    /* ---- the disc's raster, kept on the device -----------------------------

       THE SAME BYTES, NOT A NEW RENDITION (boot plan §2.5 "Avatar raster
       store", decision D11). WebKit downloaded the 835 KB portrait again on
       every visit and the disc reduced it again on the main thread (~0.6 s at
       4x CPU), all inside the boot veil. The store keeps the EXACT
       `data:image/png` string the raster sampler wrote into the disc, keyed
       by account, URL, disc size and device pixel ratio, in IndexedDB
       `moonfall-disc` (one entry per account):
         - `warm(uid)` resolves on a hit WITHOUT downloading;
         - the paint uses it only when the SERVER's account names the same
           URL at the same size and DPR (a changed face loads as before);
         - it is written at idle after a sampled paint, only when something
           changed; refused over 30 days old, over 600 KB or without the PNG
           prefix; cleared by `forget()` (sign-out, a refused login, erasure);
         - storage that throws or refuses falls back to today's path;
         - a first visit creates no database: a read never creates one (an
           upgrade a read runs into is aborted), only the first write does. */
    const DISC_STORE_NAME = 'moonfall-disc';
    const DISC_STORE_VERSION = 1;
    const DISC_STORE_FACES = 'faces';
    const DISC_RASTER_PREFIX = 'data:image/png;base64,';
    const DISC_RASTER_MAX_CHARS = 600 * 1024;
    /* A read takes 5-30 ms; one that has not answered by this is not waited
       for, and the disc loads as before. */
    const DISC_STORE_READ_CAP_MS = 250;
    /** @typedef {{ uid: string, url: string, size: number, dpr: number, raster: string, at: number }} DiscRaster */
    /** This page's read of the store, for one account.
        @type {{ uid: string, settled: boolean, entry: DiscRaster | null, promise: Promise<DiscRaster | null> } | null} */
    let discStoreRead = null;
    /** What the store holds for the account, as last read or written here. @type {DiscRaster | null} */
    let discStoreKnown = null;

    function discStoreFactory() {
        try {
            const factory = typeof window !== 'undefined' ? window.indexedDB : null;
            return factory && typeof factory.open === 'function' ? factory : null;
        } catch (_) {
            return null;
        }
    }

    function currentDpr() {
        const dpr = Number(typeof window !== 'undefined' ? window.devicePixelRatio : 1);
        return Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
    }

    function keepableRaster(raster) {
        return typeof raster === 'string'
            && raster.startsWith(DISC_RASTER_PREFIX)
            && raster.length <= DISC_RASTER_MAX_CHARS;
    }

    /** The stored entry, if it is one this boot may paint for `uid`. @returns {DiscRaster | null} */
    function validDiscRaster(entry, uid) {
        if (!entry || typeof entry !== 'object' || entry.uid !== uid) return null;
        if (typeof entry.url !== 'string' || !/^https?:\/\//.test(entry.url)) return null;
        const size = Number(entry.size);
        const dpr = Number(entry.dpr);
        if (!Number.isFinite(size) || size <= 0 || size > 1024 || !Number.isFinite(dpr) || dpr <= 0) return null;
        if (!keepableRaster(entry.raster)) return null;
        if (!(Math.abs(Date.now() - Number(entry.at)) < REMEMBERED_FACE_MAX_AGE_MS)) return null;
        return { uid, url: entry.url, size, dpr, raster: entry.raster, at: Number(entry.at) };
    }

    function closeQuietly(db) {
        try { db.close(); } catch (_) { /* already closed */ }
    }

    /** Open the store; null on any failure. Only a write creates it. */
    function openDiscStore({ create = false } = {}) {
        const factory = discStoreFactory();
        if (!factory) return Promise.resolve(null);
        return new Promise((resolveOpen) => {
            let request = null;
            try {
                request = factory.open(DISC_STORE_NAME, DISC_STORE_VERSION);
            } catch (_) {
                resolveOpen(null);
                return;
            }
            const opening = request;
            opening.onupgradeneeded = () => {
                try {
                    if (!create) throw new Error('a read creates nothing');
                    const db = opening.result;
                    if (!db.objectStoreNames.contains(DISC_STORE_FACES)) {
                        db.createObjectStore(DISC_STORE_FACES, { keyPath: 'uid' });
                    }
                } catch (_) {
                    try { opening.transaction?.abort(); } catch (__) { /* already aborted */ }
                }
            };
            opening.onsuccess = () => {
                const db = opening.result;
                let usable = false;
                try { usable = db.objectStoreNames.contains(DISC_STORE_FACES); } catch (_) { usable = false; }
                if (usable) {
                    resolveOpen(db);
                    return;
                }
                closeQuietly(db);
                resolveOpen(null);
            };
            opening.onerror = (event) => {
                // An aborted read is expected, not an error to report.
                try { event?.preventDefault?.(); } catch (_) { /* nothing to prevent */ }
                resolveOpen(null);
            };
        });
    }

    /** This page's read of the store for `uid` (one per account). */
    function readDiscStore(uid) {
        if (discStoreRead && discStoreRead.uid === uid) return discStoreRead;
        /** @type {{ uid: string, settled: boolean, entry: DiscRaster | null, promise: Promise<DiscRaster | null> }} */
        const read = { uid, settled: false, entry: null, promise: Promise.resolve(null) };
        const reading = openDiscStore().then((db) => {
            if (!db) return null;
            return new Promise((resolveRead) => {
                try {
                    const tx = db.transaction(DISC_STORE_FACES, 'readonly');
                    const get = tx.objectStore(DISC_STORE_FACES).get(uid);
                    get.onsuccess = () => resolveRead(get.result);
                    get.onerror = () => resolveRead(null);
                    tx.onabort = () => resolveRead(null);
                } catch (_) {
                    resolveRead(null);
                }
            }).then((value) => {
                closeQuietly(db);
                return value;
            });
        }).then((value) => validDiscRaster(value, uid), () => null);
        let timer = null;
        const capped = new Promise((resolveCap) => { timer = setTimeout(() => resolveCap(null), DISC_STORE_READ_CAP_MS); });
        read.promise = Promise.race([reading, capped]).then((entry) => {
            clearTimeout(timer);
            read.settled = true;
            read.entry = entry;
            if (entry && discStoreRead === read) discStoreKnown = entry;
            return entry;
        });
        discStoreRead = read;
        return read;
    }

    /**
     * The stored raster the disc may paint for `url` at `size` under `uid`:
     * null when the store has nothing to say (no store, nothing remembered for
     * this face, or a read already answered with something else), so the
     * disc loads as it always has, in this task; otherwise a promise of the
     * raster, or of null when it does not match.
     * @returns {Promise<string | null> | null}
     */
    function storedDiscRaster(uid, url, size) {
        if (!uid || !discStoreFactory()) return null;
        let read = discStoreRead && discStoreRead.uid === uid ? discStoreRead : null;
        if (!read) {
            // Nothing is read for a face this browser never showed the account.
            const face = recallFace(uid);
            if (!face || face.url !== url) return null;
            read = readDiscStore(uid);
        }
        const dpr = currentDpr();
        const matches = (entry) => Boolean(entry && entry.url === url && entry.size === size && entry.dpr === dpr);
        if (read.settled && !matches(read.entry)) return null;
        return read.promise.then((entry) => (matches(entry) ? entry.raster : null));
    }

    function whenIdle(work) {
        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(() => work(), { timeout: 2000 });
        } else {
            setTimeout(work, 0);
        }
    }

    /* After a sampled paint, at idle, and only when something changed: the
       raster the disc shows, for the account the paint started under, unless
       anything was forgotten since. */
    function keepDiscRaster(url, size, ticket, raster) {
        const uid = ticket.uid;
        if (!uid || ticket.forgets !== forgets || !keepableRaster(raster) || !discStoreFactory()) return;
        const dpr = currentDpr();
        const known = discStoreKnown;
        if (known && known.uid === uid && known.url === url && known.size === size && known.dpr === dpr && known.raster === raster) return;
        /** @type {DiscRaster} */
        const entry = { uid, url, size, dpr, raster, at: Date.now() };
        const current = () => accountId() === uid && ticket.forgets === forgets;
        whenIdle(() => {
            if (!current()) return;
            openDiscStore({ create: true }).then((db) => {
                if (!db) return;
                if (!current()) {
                    closeQuietly(db);
                    return;
                }
                try {
                    const tx = db.transaction(DISC_STORE_FACES, 'readwrite');
                    tx.objectStore(DISC_STORE_FACES).put(entry);
                    tx.oncomplete = () => {
                        closeQuietly(db);
                        if (current()) discStoreKnown = entry;
                    };
                    tx.onerror = () => closeQuietly(db);
                    tx.onabort = () => closeQuietly(db);
                } catch (_) {
                    closeQuietly(db);
                }
            }, () => {});
        });
    }

    function clearDiscStore() {
        discStoreRead = null;
        discStoreKnown = null;
        const factory = discStoreFactory();
        if (!factory || typeof factory.deleteDatabase !== 'function') return;
        try {
            const request = factory.deleteDatabase(DISC_STORE_NAME);
            if (request) {
                request.onerror = (event) => {
                    try { event?.preventDefault?.(); } catch (_) { /* nothing to prevent */ }
                };
            }
        } catch (_) { /* storage refused: nothing was kept */ }
    }

    /** Show a stored raster in `img`: true once it loaded, false if it cannot. */
    function showStoredRaster(img, raster) {
        return new Promise((resolveShown) => {
            const finish = (ok) => {
                img.removeEventListener('load', onLoad);
                img.removeEventListener('error', onError);
                resolveShown(ok);
            };
            const onLoad = () => finish(true);
            const onError = () => finish(false);
            img.addEventListener('load', onLoad);
            img.addEventListener('error', onError);
            img.setAttribute('src', raster);
        });
    }

    /** The face remembered for `uid` — null for another account, nothing
        remembered, a record that is not a face, or one too old to guess on. */
    function recallFace(uid) {
        if (typeof uid !== 'string' || !uid) return null;
        let face = null;
        try {
            face = JSON.parse(localStorage.getItem(REMEMBERED_FACE_KEY) || 'null');
        } catch (_) {
            return null;
        }
        if (!face || face.uid !== uid || typeof face.url !== 'string' || !/^https?:\/\//.test(face.url)) return null;
        const size = Number(face.size);
        if (!Number.isFinite(size) || size <= 0 || size > 1024) return null;
        // A clock set back a little is not an old record; NaN (no date) is.
        if (!(Math.abs(Date.now() - Number(face.at)) < REMEMBERED_FACE_MAX_AGE_MS)) return null;
        const view = currentView();
        return {
            url: face.url,
            size: Math.round(size),
            sameView: face.width === view.width && face.height === view.height,
        };
    }

    /**
     * Start the face this disc showed `uid` last time, before the account
     * read names it: its download, and — in the viewport it was measured in —
     * its reduction to the disc's size, so the disc paints from the sampler's
     * finished raster. In another viewport the disc will measure another size
     * and a raster for the old one would never be shown, so only the bytes
     * start. A face the account no longer names is dropped when the disc is
     * painted (`paint`). Resolves true once the face is ready, false when
     * there is nothing to warm, it could not load or it was dropped. Never
     * rejects.
     * @param {string} [uid]
     * @returns {Promise<boolean>}
     */
    function warm(uid) {
        const face = recallFace(uid);
        if (!face) return Promise.resolve(false);
        if (warming && warming.url === face.url && !warming.dropped && warming.result) return warming.result;
        if (warming) warming.drop();
        const sampler = typeof window !== 'undefined' ? window.MoonfallRasterSampler : null;
        /** @type {{ url: string, picture: HTMLImageElement | null, settled: boolean, dropped: boolean, drop: () => void, result: Promise<boolean> | null }} */
        const run = { url: face.url, picture: null, settled: false, dropped: false, drop: () => {}, result: null };
        warming = run;
        run.drop = () => {
            run.dropped = true;
            if (warming === run) warming = null;
        };
        /* THE STORE FIRST (the disc's raster, kept on the device): the very
           bytes this disc showed, at this size and DPR, need no download. */
        if (discStoreFactory()) {
            const read = readDiscStore(String(uid));
            run.result = read.promise.then((entry) => {
                if (run.dropped) return false;
                if (entry && entry.url === face.url && face.sameView && entry.size === face.size && entry.dpr === currentDpr()) {
                    run.settled = true;
                    return true;
                }
                return startWarmDownload(run, face, sampler);
            }, () => startWarmDownload(run, face, sampler));
            return run.result;
        }
        run.result = startWarmDownload(run, face, sampler);
        return run.result;
    }

    /**
     * Today's warm: the face's download and, in the same viewport, its
     * reduction. Resolves true once ready. Never rejects.
     * @param {{ url: string, picture: HTMLImageElement | null, settled: boolean, dropped: boolean, drop: () => void, result: Promise<boolean> | null }} run
     * @param {{ url: string, size: number, sameView: boolean }} face
     * @param {any} sampler
     * @returns {Promise<boolean>}
     */
    function startWarmDownload(run, face, sampler) {
        if (run.dropped) return Promise.resolve(false);
        /* The bytes load HERE, in the disc's own CORS mode, so the disc's
           request is answered from this copy. Not through the sampler's own
           load: that one gives up after 3 s and CANCELS its image, which on a
           slow link threw away the only download of the face while the disc
           still waited for it. */
        const loaded = new Promise((resolveLoad) => {
            run.drop = () => {
                run.dropped = true;
                if (warming === run) warming = null;
                const picture = run.picture;
                /* A download still in flight stops where the engine can: the
                   account's real face is loading beside it. A picture already
                   in is left whole — the sampler may hold it as the decoded
                   source of its URL. */
                if (picture && !run.settled) {
                    picture.onload = null;
                    picture.onerror = null;
                    try { picture.removeAttribute('src'); } catch (_) { /* already gone */ }
                }
                resolveLoad(false);
            };
            try {
                const picture = new Image();
                run.picture = picture;
                picture.crossOrigin = 'anonymous';
                picture.decoding = 'async';
                picture.onload = () => { run.settled = true; resolveLoad(true); };
                picture.onerror = () => { run.settled = true; resolveLoad(false); };
                picture.src = face.url;
            } catch (_) {
                resolveLoad(false);
            }
        });
        return loaded.then((ready) => {
            if (!ready || run.dropped) return false;
            if (!sampler) return true;
            if (typeof sampler.rememberDecodedImage === 'function') {
                try { sampler.rememberDecodedImage(face.url, run.picture); } catch (_) { /* sampler optional */ }
            }
            if (!face.sameView || typeof sampler.sample !== 'function') return true;
            let task = null;
            try {
                // The disc's own request: one sampler cache entry for both.
                task = sampler.sample(face.url, discRequest(face.size, face.size));
            } catch (_) {
                return false;
            }
            return Promise.resolve(task).then((result) => Boolean(result && !result.fallback), () => false);
        });
    }

    /**
     * The sampled raster for a portrait in a small disc, when the game's
     * sampler is present; the direct picture otherwise. Resolves once the
     * picture is painted (either way) or rejects when it cannot load.
     */
    function paintPortrait(img, url, { width, height }) {
        const sampler = typeof window !== 'undefined' ? window.MoonfallRasterSampler : null;
        return new Promise((resolveLoad, rejectLoad) => {
            img.onload = () => resolveLoad(true);
            img.onerror = () => rejectLoad(new Error('avatar_unreachable'));
            /* ONE download, not two. The sampler needs a CORS-clean copy to
               downsample the portrait into the disc; a plain <img> load is
               no-cors, so the sampler used to fetch the same ~500 KB picture a
               second time, after the first (measured on a phone boot: 1.2 s
               then 0.8 s, both on the lobby's critical path). The asset CDN
               always answers with `access-control-allow-origin: *`, so the
               disc can load in CORS mode and hand its decoded image over. */
            img.crossOrigin = 'anonymous';
            img.src = url;
        }).then(() => {
            if (sampler && typeof sampler.rememberDecodedImage === 'function') {
                try { sampler.rememberDecodedImage(url, img); } catch (_) { /* sampler optional */ }
            }
            if (!sampler || typeof sampler.enhanceImage !== 'function') return true;
            return Promise.resolve(sampler.enhanceImage(img, url, discRequest(width, height)))
                .then(() => true, () => true);
        });
    }

    /** Resolves once `img`'s current picture is loaded and decoded (never
        rejects). Loaded FIRST: asked right after the source changed, Firefox
        answers `decode()` for the picture being replaced — it resolves (or
        refuses) while the new one is still incomplete — where Chromium and
        WebKit wait for the new one (measured 26 September 2026). */
    function decoded(img) {
        if (typeof img.decode !== 'function') return Promise.resolve();
        const loaded = img.complete ? Promise.resolve() : new Promise((resolveLoad) => {
            img.addEventListener('load', resolveLoad, { once: true });
            img.addEventListener('error', resolveLoad, { once: true });
        });
        return loaded.then(() => img.decode()).then(() => {}, () => {});
    }

    /**
     * Make `work` the disc's paint: `whenPainted()` settles with it. A wait
     * taken before a newer paint replaced this one follows the newer one
     * instead — a replaced paint may never settle on its own (the moon
     * removes its source, a new face takes its load handlers), and the boot
     * veil takes its wait once, right after the mount (review, 26 September
     * 2026: the veil sat out its cap).
     * @param {Promise<unknown>} work
     */
    function track(work) {
        /** @type {(value?: unknown) => void} */
        let settle = () => {};
        const tracked = new Promise((resolvePaint) => {
            settle = resolvePaint;
            work.then(() => resolvePaint(), () => resolvePaint());
        });
        // Already settled, the replaced paint ignores this.
        if (handOver) handOver(tracked);
        handOver = settle;
        painted = tracked;
    }

    /**
     * Paint `url` into a detached copy of the disc's picture and put it in the
     * disc's place once it is painted. A face that cannot load leaves the
     * moon: the server has already said the old face is not the avatar.
     */
    function swapPortrait(url, size, view) {
        const current = image;
        const number = ++paintNumber;
        const ticket = rememberTicket();
        current.dataset.avatarSource = url;
        const next = /** @type {HTMLImageElement} */ (current.cloneNode(false));
        next.removeAttribute('src');
        next.hidden = false;
        track(paintPortrait(next, url, { width: size, height: size })
            .then(() => decoded(next))
            .then(() => {
                if (number !== paintNumber || !current.isConnected) return;
                current.replaceWith(next);
                image = next;
                button.classList.add('has-portrait');
                rememberFace(url, size, view, ticket);
                keepDiscRaster(url, size, ticket, next.getAttribute('src'));
            }, () => {
                if (number !== paintNumber) return;
                current.hidden = true;
                button.classList.remove('has-portrait');
                // Named again, the face loads again rather than showing broken.
                delete current.dataset.avatarSource;
                forgetFace();
            }));
    }

    /** Paint the disc from a profile. The moon shows only until the picture is in. */
    function paint(profile) {
        if (!resolve()) return false;
        const url = typeof profile?.avatarPortraitUrl === 'string' && profile.avatarPortraitUrl ? profile.avatarPortraitUrl : '';
        currentCharacterId = typeof profile?.avatarCharacterId === 'string' ? profile.avatarCharacterId : null;
        button.dataset.avatarCharacterId = currentCharacterId || '';
        button.dataset.avatarDefault = profile?.avatarIsDefault ? 'true' : 'false';
        /* The boot warm guessed a face the account no longer shows (changed
           on another device, or the default moved): drop it, so its download
           does not compete with the real face's and it is never reduced. */
        if (warming && warming.url !== url) warming.drop();
        if (url) {
            if (image.dataset.avatarSource !== url) {
                const box = button.getBoundingClientRect();
                const view = currentView();
                // The picture is drawn at 116% of the disc (hub.css frames the
                // face); the raster is sampled at that size so it is never
                // scaled up afterwards.
                const size = Math.max(56, Math.round((Math.max(box.width, box.height) || 0) * 1.16));
                /* A disc already showing a face keeps it until the next face
                   is painted, off screen, and then swaps in one step. Hiding
                   it first left an empty disc — under the "Avatar updated"
                   toast — for the whole download and resample. */
                if (button.classList.contains('has-portrait') && !image.hidden && image.getAttribute('src')) {
                    swapPortrait(url, size, view);
                    return true;
                }
                const number = ++paintNumber;
                const ticket = rememberTicket();
                image.dataset.avatarSource = url;
                image.hidden = true;
                /* SHOWN ONCE LOADED AND DECODED, like a replaced face
                   (swapPortrait). The sampler's last step writes its reduced
                   raster, a data URL the engine has not loaded or decoded
                   yet: showing the disc then put "1 picture loading in front"
                   on WebKit's cold boot for 192-262 ms (round 13). The boot
                   veil waits on `painted`, within its own cap, so this adds
                   only that load and decode. */
                const portrait = () => paintPortrait(image, url, { width: size, height: size });
                /* The raster this disc showed last time, kept on the device,
                   when the server names the same face at the same size and
                   DPR: no download, no reduction. One that will not load
                   is dropped and the face loads as before. */
                const stored = storedDiscRaster(ticket.uid, url, size);
                const work = stored
                    ? stored.then((raster) => {
                        // A newer paint owns the picture now.
                        if (number !== paintNumber) return undefined;
                        if (!raster) return portrait();
                        return showStoredRaster(image, raster).then((shown) => {
                            if (shown || number !== paintNumber) return undefined;
                            clearDiscStore();
                            return portrait();
                        });
                    })
                    : portrait();
                track(work.then(() => decoded(image)).then(() => {
                    if (number !== paintNumber) return;
                    image.hidden = false;
                    button.classList.add('has-portrait');
                    // The face, the size it was reduced for and the viewport
                    // it was measured in: the next boot starts exactly this
                    // raster with the sign-in.
                    rememberFace(url, size, view, ticket);
                    keepDiscRaster(url, size, ticket, image.getAttribute('src'));
                    // Painted: the warm's copy no longer needs holding.
                    if (warming && warming.url === url) warming = null;
                }, () => {
                    if (number !== paintNumber) return;
                    image.hidden = true;
                    button.classList.remove('has-portrait');
                    // Named again, the face loads again rather than showing broken.
                    delete image.dataset.avatarSource;
                    forgetFace();
                }));
            }
            /* The same face named again is on screen already, or is a paint
               in flight that shows it once loaded and decoded. Un-hiding it
               here showed a picture still downloading or decoding. */
        } else {
            delete image.dataset.avatarSource;
            image.removeAttribute('src');
            image.hidden = true;
            button.classList.remove('has-portrait');
            /* The moon is the final face, painted now. A wait taken on a paint
               still in flight settles with it (`track`): that paint lost its
               source above, so no load or error would ever settle it. */
            paintNumber += 1;
            track(Promise.resolve());
            // The moon: no face for the next boot to start.
            forgetFace();
        }
        return true;
    }

    function mount(profile) {
        if (!resolve()) return false;
        button.style.display = '';
        paint(profile);
        if (!bound) {
            bound = true;
            button.addEventListener('click', (event) => {
                event.preventDefault();
                open();
            });
            // The intent that precedes the click: never at lobby idle, and
            // never the focus close() gives back (givingFocusBack).
            const warmPicker = (event) => {
                if (event?.type === 'focus' && givingFocusBack) return;
                void warmFaces();
            };
            ['pointerenter', 'focus', 'pointerdown'].forEach((type) => {
                button.addEventListener(type, warmPicker, { passive: true });
            });
        }
        return true;
    }

    async function authHeaders() {
        try {
            const token = await Firebase?.getIdToken?.();
            return token ? { Authorization: `Bearer ${token}` } : {};
        } catch (_) {
            return {};
        }
    }

    /** The route's rows that can be faces: a summoner with a portrait. */
    function facesOf(rows) {
        return (Array.isArray(rows) ? rows : [])
            .map((item) => ({
                characterId: typeof item?.characterId === 'string' ? item.characterId : '',
                name: typeof item?.name === 'string' ? item.name : '',
                imageUrl: typeof item?.imageUrl === 'string' && /^https?:\/\//.test(item.imageUrl) ? item.imageUrl : '',
            }))
            .filter((item) => item.characterId && item.imageUrl);
    }

    /**
     * One page of the account's summoners, from the route's cursor (none for
     * the first). `isDone` is the route's word that nothing follows.
     * @param {string | null} cursor
     * @returns {Promise<{ items: Array<{ characterId: string, name: string, imageUrl: string }>, nextCursor: string | null, isDone: boolean }>}
     */
    async function fetchSummoners(cursor = null) {
        const headers = await authHeaders();
        /* The route caps a read at 24 rows; a page of the DIALOG may hold
           more or fewer than that, which `readUntil` settles by reading
           again. */
        const params = new URLSearchParams({ limit: '24' });
        params.set('lang', i18n?.getLanguage?.() || 'en');
        if (cursor) params.set('cursor', cursor);
        const response = await fetch(`/api/deck-builder/characters-page?${params.toString()}`, { headers });
        if (!response.ok) throw new Error('characters_unavailable');
        const data = await response.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        const nextCursor = typeof data?.nextCursor === 'string' && data.nextCursor ? data.nextCursor : null;
        return {
            items: facesOf(items),
            nextCursor,
            isDone: Boolean(data?.isDone) || !nextCursor,
        };
    }

    function facesKey() {
        let uid = '';
        try { uid = String(Firebase?.getUser?.()?.uid || ''); } catch (_) { uid = ''; }
        return `${uid}|${i18n?.getLanguage?.() || 'en'}`;
    }

    /* The first route page IS the shared roster (summoner-roster.js): the
       summoner select read it too, so it is never asked for twice. */
    function seedFromRoster(record, roster) {
        if (!roster || record.faces.length || record.done || record.cursor) return;
        record.faces.push(...facesOf(roster.rows));
        record.cursor = roster.nextCursor;
        record.done = roster.isDone || !roster.nextCursor;
    }

    /** The faces read for this account and language, a fresh record otherwise. */
    function readFaces() {
        const key = facesKey();
        if (!facesRead || facesRead.key !== key) {
            facesRead = { key, faces: [], cursor: null, done: false };
            const roster = SummonerRoster.peek();
            if (roster && roster.key === key) seedFromRoster(facesRead, roster);
        }
        return facesRead;
    }

    /* A roster made again (a summoner made or deleted, or another device's
       change found behind a view) makes the faces kept from the old one
       stale: a closed picker forgets them, and its next warm or open reads
       the new list. An open picker re-reads behind itself (revalidateFaces). */
    SummonerRoster.onChange(() => {
        if (!dialog) facesRead = null;
    });

    /** The first route page for `record`: the shared roster, read once. */
    function readFirstPage(record) {
        return SummonerRoster.read().promise.then((roster) => {
            if (facesRead === record) seedFromRoster(record, roster);
        });
    }

    /* ---- the faces' pictures ----------------------------------------------

       A face is shown as the game's sampled raster (the same request every
       time: `tileRequest`). Its portrait is downloaded ONCE, in CORS mode, into
       a detached copy the page never shows; the sampler is handed that copy
       and the tile gets only the finished raster. Handing the sampler the
       tile's own <img> made the tile its "decoded source" for the portrait,
       and the tile then turned into an 84 px raster under the sampler's feet.
       A face whose raster is known this session is asked of the sampler
       straight away, with no download. */

    /** Portraits whose tile raster the sampler has made this session. */
    const tileSampled = new Set();
    /** @type {Map<string, { image: HTMLImageElement | null, users: number, settled: boolean, promise: Promise<HTMLImageElement | null>, finish: (image: HTMLImageElement | null) => void }>} */
    const originals = new Map();
    /** Faces whose warm (on intent) is on its way. */
    const warmingFaces = new Set();
    /* How many faces one page holds in a viewport (its CSS size): measured
       once, by the first layout there, so the warm knows what ONE page is. */
    /** @type {Map<string, number>} */
    const pageCapacity = new Map();

    function samplerOf() {
        const sampler = typeof window !== 'undefined' ? window.MoonfallRasterSampler : null;
        return sampler && typeof sampler.sample === 'function' ? sampler : null;
    }

    function tileRequest() {
        return discRequest(TILE_RASTER_PX, TILE_RASTER_PX);
    }

    function viewKey() {
        const view = currentView();
        return `${view.width}x${view.height}`;
    }

    function settleOriginal(url, entry, picture) {
        if (entry.settled) return;
        entry.settled = true;
        if (originals.get(url) === entry) originals.delete(url);
        entry.finish(picture);
    }

    /**
     * One download of `url` in CORS mode, shared by every caller that asks
     * while it is on its way. Resolves with the loaded picture, or null when
     * it cannot load. A lease released by its last holder before the picture
     * is in stops the download where the engine can: nobody is going to see
     * it (the dialog closed, the player turned away from that page).
     * @param {string} url
     * @param {{ low?: boolean }} [options] a page fetched ahead of the player
     */
    function leaseOriginal(url, { low = false } = {}) {
        let entry = originals.get(url);
        if (!entry) {
            /** @type {(image: HTMLImageElement | null) => void} */
            let finish = () => {};
            const promise = new Promise((resolveLoad) => { finish = resolveLoad; });
            const created = { image: null, users: 0, settled: false, promise, finish };
            entry = created;
            originals.set(url, created);
            try {
                const picture = new Image();
                created.image = picture;
                picture.crossOrigin = 'anonymous';
                picture.decoding = 'async';
                if (low) {
                    try { picture.setAttribute('fetchpriority', 'low'); } catch (_) { /* a hint only */ }
                }
                picture.onload = () => settleOriginal(url, created, picture.naturalWidth > 0 ? picture : null);
                picture.onerror = () => settleOriginal(url, created, null);
                picture.src = url;
            } catch (_) {
                settleOriginal(url, created, null);
            }
        }
        const held = entry;
        held.users += 1;
        let released = false;
        return {
            promise: held.promise,
            release() {
                if (released) return;
                released = true;
                held.users -= 1;
                if (held.users > 0 || held.settled) return;
                const picture = held.image;
                if (picture) {
                    picture.onload = null;
                    picture.onerror = null;
                    try { picture.removeAttribute('src'); } catch (_) { /* already gone */ }
                }
                settleOriginal(url, held, null);
            },
        };
    }

    /** The tile raster of `url`: its data URL, the portrait itself when the
        sampler draws it directly, or null when the sampler could not. */
    function sampleTile(sampler, url) {
        let task = null;
        try {
            task = sampler.sample(url, tileRequest());
        } catch (_) {
            return Promise.resolve(null);
        }
        return Promise.resolve(task).then((result) => {
            if (!result || result.fallback) return null;
            if (result.sampled && typeof result.url === 'string' && result.url) {
                tileSampled.add(url);
                return result.url;
            }
            return url;
        }, () => null);
    }

    function rememberOriginal(sampler, url, picture) {
        if (typeof sampler.rememberDecodedImage !== 'function') return;
        try { sampler.rememberDecodedImage(url, picture); } catch (_) { /* sampler optional */ }
    }

    /**
     * The pictures one page asked for. `paint(img, url)` resolves once the
     * <img> shows its face's final raster, loaded and decoded — or once the
     * face turns out not to load, which leaves the disc's empty well (never a
     * broken picture). Never rejects. `close()` drops every download still on
     * its way that nothing else waits for.
     * @param {{ low?: boolean }} [options]
     */
    function createFaceSession({ low = false } = {}) {
        /** @type {Set<{ release: () => void }>} */
        const leases = new Set();
        let closed = false;
        /* Portraits this page shows by their own <img> (no sampler), still
           downloading. */
        let directLoads = 0;

        /** @param {HTMLImageElement} img @param {string} url */
        function paint(img, url) {
            const sampler = samplerOf();
            let source;
            if (!sampler) {
                // The portrait itself, in the copy's CORS mode: loaded, then decoded.
                directLoads += 1;
                img.crossOrigin = 'anonymous';
                img.setAttribute('src', url);
                return decoded(img).then(() => { directLoads -= 1; });
            } else if (tileSampled.has(url)) {
                source = sampleTile(sampler, url).then((src) => src || url);
            } else {
                const lease = leaseOriginal(url, { low });
                leases.add(lease);
                source = lease.promise.then((picture) => {
                    leases.delete(lease);
                    lease.release();
                    if (!picture || closed) return null;
                    return decodedOriginal(picture).then(() => {
                        if (closed) return null;
                        rememberOriginal(sampler, url, picture);
                        return sampleTile(sampler, url).then((src) => src || url);
                    });
                });
            }
            return source.then((src) => {
                if (closed || !src) return undefined;
                // The portrait itself (the sampler's fallback) loads in the copy's CORS mode.
                img.crossOrigin = 'anonymous';
                img.setAttribute('src', src);
                return decoded(img);
            }, () => undefined);
        }

        return {
            paint,
            /** A portrait of this page still downloading. */
            onTheWire: () => leases.size > 0 || directLoads > 0,
            close() {
                closed = true;
                leases.forEach((lease) => lease.release());
                leases.clear();
            },
        };
    }

    const faceImage = (tile) => /** @type {HTMLImageElement | null} */ (tile.querySelector('img.avatar-picker-image'));
    /** @type {WeakMap<Element, string>} */
    const tileUrls = new WeakMap();

    /** Paint every tile's face; resolves once all of them are in (or out). */
    function paintTiles(tiles, session) {
        return Promise.all(tiles.map((tile) => {
            const img = faceImage(tile);
            const url = tileUrls.get(tile);
            return img && url ? session.paint(img, url) : undefined;
        })).then(() => undefined);
    }

    /** `work`, or nothing once `ms` have passed. */
    function bounded(work, ms) {
        let timer = null;
        const limit = new Promise((resolveLimit) => { timer = setTimeout(resolveLimit, Math.max(0, ms)); });
        return Promise.race([work, limit]).then(() => { clearTimeout(timer); });
    }

    /* The names are measured in the hub's serif. Laying the page out asks
       for any face of it not yet loaded; a page measured in the fallback type
       would believe four rows fit where three do. */
    function namesFontsPending() {
        const fonts = typeof document !== 'undefined' ? document.fonts : null;
        if (!fonts) return null;
        let missing = fonts.status === 'loading';
        try {
            if (!missing && typeof fonts.check === 'function') missing = !fonts.check('600 16px Spectral');
        } catch (_) { /* check is a hint */ }
        if (!missing) return null;
        const load = typeof fonts.load === 'function'
            ? Promise.resolve(fonts.load('600 16px Spectral')).catch(() => null)
            : Promise.resolve(null);
        return load.then(() => fonts.ready).then(() => undefined, () => undefined);
    }

    /* ---- the page's layout ------------------------------------------------ */

    /**
     * How many of the rendered tiles `grid` can show whole.
     *
     * Read from the laid-out grid rather than from constants: the column
     * count is the resolved track list (the CSS decides it from the tile
     * width), and the row height is a real tile, names and all. Returns null
     * where there is no layout to read — jsdom, or a dialog that has not been
     * laid out — and the caller then shows what it has rather than trimming
     * on a guess.
     * @returns {number | null}
     */
    function fitCountOf(grid) {
        const first = grid.firstElementChild;
        if (!(first instanceof HTMLElement)) return null;
        const style = window.getComputedStyle(grid);
        const columns = String(style.gridTemplateColumns || '').trim().split(/\s+/).filter(Boolean).length;
        const rowGap = parseFloat(style.rowGap) || 0;
        const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
        /* The TALLEST tile decides the row: a two-line name is a taller tile
           than a one-line name beside it, and the grid's rows are sized to
           their content. */
        let tallest = 0;
        for (const child of grid.children) {
            if (child instanceof HTMLElement) tallest = Math.max(tallest, child.offsetHeight);
        }
        const room = grid.clientHeight - padding;
        if (!columns || !tallest || room <= 0) return null;
        const rows = Math.max(1, Math.floor((room + rowGap) / (tallest + rowGap)));
        return columns * rows;
    }

    /**
     * NO NAME IS EVER CUT. The size a name needs is a question about the
     * font's metrics in THIS engine — Firefox wrapped "Tidewarden of the
     * Drowned Bell" onto a line more than Chromium did at the same size — so
     * the answer is measured rather than guessed: a name that overflows its
     * box steps down until it fits, and never below the 10px the repo treats
     * as the legibility floor. Nothing moves for the common case, where the
     * first measurement already fits.
     */
    function fitNamesIn(grid) {
        const FLOOR = uiPx(10);
        for (const label of grid.querySelectorAll('.avatar-picker-name')) {
            if (!(label instanceof HTMLElement)) continue;
            if (label.scrollHeight <= label.clientHeight + 1) continue;
            let size = parseFloat(window.getComputedStyle(label).fontSize) || 0;
            if (!size) continue;
            while (size > FLOOR && label.scrollHeight > label.clientHeight + 1) {
                size = Math.max(FLOOR, size - 0.5);
                label.style.fontSize = `${size}px`;
            }
        }
    }

    /**
     * The dialog's parts, unattached. A probe (`probe: true`) is the same
     * dialog with no identity, measured unseen and removed in one task.
     */
    function buildShell({ probe = false } = {}) {
        const overlay = document.createElement('div');
        overlay.className = probe ? 'avatar-picker avatar-picker--probe' : 'avatar-picker';
        if (probe) overlay.setAttribute('aria-hidden', 'true');
        else overlay.id = DIALOG_ID;
        const card = document.createElement('div');
        card.className = 'avatar-picker-card';
        if (!probe) {
            card.setAttribute('role', 'dialog');
            card.setAttribute('aria-modal', 'true');
            card.setAttribute('aria-labelledby', `${DIALOG_ID}-title`);
        }

        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'avatar-picker-close hub-sheet-close';
        closeButton.setAttribute('aria-label', uiText('hub.close', 'Close'));
        const closeGlyph = document.createElement('span');
        closeGlyph.setAttribute('aria-hidden', 'true');
        closeGlyph.textContent = '×';
        closeButton.appendChild(closeGlyph);

        const ornament = document.createElement('span');
        ornament.className = 'hub-sheet-ornament';
        ornament.setAttribute('aria-hidden', 'true');
        ornament.textContent = '✦ ☾ ✦';

        const title = document.createElement('h2');
        if (!probe) title.id = `${DIALOG_ID}-title`;
        title.className = 'hub-sheet-title';
        title.textContent = uiText('account.avatarTitle', 'Choose your avatar');

        const hint = document.createElement('p');
        hint.className = 'avatar-picker-hint';
        hint.textContent = uiText('account.avatarHint', 'Any summoner you own can be your face on the hub.');

        const grid = document.createElement('div');
        grid.className = 'avatar-picker-grid';

        /* The pager: Prev · Page N · Next, under the grid, shown only once
           the collection has a second page. */
        const pager = document.createElement('nav');
        pager.className = 'avatar-picker-pager';
        pager.hidden = true;
        const prevButton = document.createElement('button');
        prevButton.type = 'button';
        prevButton.disabled = true;
        prevButton.className = 'avatar-picker-prev';
        prevButton.textContent = `‹ ${uiText('common.prev', 'Prev')}`;
        const pageLabel = document.createElement('span');
        pageLabel.className = 'avatar-picker-page';
        pageLabel.setAttribute('aria-live', 'polite');
        const nextButton = document.createElement('button');
        nextButton.type = 'button';
        nextButton.disabled = true;
        nextButton.className = 'avatar-picker-next';
        nextButton.textContent = `${uiText('common.next', 'Next')} ›`;
        pager.append(prevButton, pageLabel, nextButton);

        card.append(closeButton, ornament, title, hint, grid, pager);
        overlay.appendChild(card);
        return { overlay, card, closeButton, grid, pager, prevButton, pageLabel, nextButton };
    }

    /**
     * How many faces the first page holds in this viewport: the real dialog,
     * laid out unseen with every face it could hold, measured and removed in
     * the same task (so no frame ever shows it). Null where there is no
     * layout to read; nothing is warmed on a guess.
     * @param {Array<{ characterId: string, name: string, imageUrl: string }>} faces
     * @param {boolean} done
     * @returns {number | null}
     */
    function firstPageCapacity(faces, done) {
        const key = viewKey();
        if (pageCapacity.has(key)) return pageCapacity.get(key) ?? null;
        if (typeof document === 'undefined' || !document.body) return null;
        const shell = buildShell({ probe: true });
        document.body.appendChild(shell.overlay);
        let fits = null;
        try {
            shell.grid.replaceChildren(...faces.slice(0, MAX_PAGE).map((summoner) => buildOption(summoner)));
            fitNamesIn(shell.grid);
            fits = fitCountOf(shell.grid);
            if (fits !== null && (faces.length > fits || !done)) {
                // A collection with a second page shows its pager, in the grid's room.
                shell.pager.hidden = false;
                const again = fitCountOf(shell.grid);
                if (again !== null) fits = Math.min(fits, again);
            }
        } finally {
            shell.overlay.remove();
        }
        if (fits !== null) pageCapacity.set(key, fits);
        return fits;
    }

    /* ---- the warm, on intent and at lobby idle ----------------------------- */

    /* ONE DECODE, OFF THE MAIN THREAD (round 17, 28 September 2026). The
       sampler reduces a portrait by drawing it, and a picture drawn while
       still undecoded is decoded inside that draw, on the main thread: a
       2752×1536 summoner portrait at 4x CPU made the picker's open spend
       ~255 ms a face (Chrome phone seat, 4G, 1.3 s for five faces with every
       byte already cached). `decode()` does the same decode off the main
       thread first, and the draw then reads pixels already there — the same
       pixels, a shorter task. Capped: a decode that never settles is not
       waited on (the draw then decodes as before). */
    const ORIGINAL_DECODE_CAP_MS = 1500;

    function decodedOriginal(picture) {
        if (!picture || typeof picture.decode !== 'function') return Promise.resolve(picture);
        const decoding = Promise.resolve().then(() => picture.decode()).catch(() => {});
        return bounded(decoding, ORIGINAL_DECODE_CAP_MS).then(() => picture);
    }

    /** Download, decode and sample one face's tile raster; never rejects. */
    function sampleFace(url, { low = false } = {}) {
        const sampler = samplerOf();
        if (!sampler || tileSampled.has(url) || warmingFaces.has(url)) return Promise.resolve();
        warmingFaces.add(url);
        const lease = leaseOriginal(url, { low });
        return lease.promise.then((picture) => {
            lease.release();
            if (!picture) return null;
            return decodedOriginal(picture).then(() => {
                rememberOriginal(sampler, url, picture);
                return sampleTile(sampler, url);
            });
        }).catch(() => null).finally(() => {
            warmingFaces.delete(url);
        });
    }

    function warmTileFace(url) {
        void sampleFace(url);
    }

    /* The first page's faces, sampled as the tiles will ask for them: ONE
       page (measured, never guessed), and only while no dialog is up (an
       open dialog paints its own). */
    function warmFirstPage(record) {
        if (dialog || facesRead !== record || !record.faces.length || !samplerOf()) return;
        const fits = firstPageCapacity(record.faces, record.done);
        if (!fits) return;
        record.faces.slice(0, fits).forEach((summoner) => warmTileFace(summoner.imageUrl));
    }

    /* Pointing at (or focusing, or pressing) the disc is the intent that
       precedes the click: the first route page is read then, and the faces
       of the page it will open on are downloaded and sampled, so the first
       open usually has nothing left to wait for. */
    function warmFaces() {
        const record = readFaces();
        let reading;
        if (record.faces.length || record.done) {
            reading = Promise.resolve();
        } else if (record.pending) {
            reading = record.pending;
        } else {
            record.pending = readFirstPage(record).catch(() => {}).finally(() => {
                record.pending = null;
            });
            reading = record.pending;
        }
        return reading.then(() => warmFirstPage(record));
    }

    /* THE FACES, AHEAD, AT LOBBY IDLE (owner, 28 September 2026: the
       account's summoners are a small set — "you could load like at least
       20" — so the picker opens and pages from memory). Measured before
       (round 17 probe, the seven default summoners): the open read the route
       and sampled every face at the press, 0.93-1.05 s on the cable desktop
       and 1.7-2.2 s on the 4G phones, where the route read alone was 0.8-
       0.9 s. Now the lobby's warm (lobby.js, behind the summoner select's
       own) takes the faces of the first LOBBY_WARM_PAGES pages from the
       shared roster and samples them ONE AT A TIME, each in an idle slot at
       least 400 ms after the player's last input, and only while the lobby
       is still the screen (`stillIdle`) and no dialog is up (an open dialog
       paints its own). The portraits are the summoner select's: already
       downloaded by it, they come from the cache. Once per roster; a changed
       roster warms again. Never rejects. */
    const LOBBY_WARM_PAGES = 2;
    /** @type {{ roster: object, promise: Promise<void> } | null} */
    let lobbyWarm = null;

    function idleSlot() {
        return new Promise((resolveSlot) => whenIdle(() => resolveSlot(undefined)));
    }

    /* A face's reduction is one main-thread task (~80 ms at 4x CPU once its
       picture is decoded), so the lobby's warm starts one only after the
       player has left the page alone this long: a press never waits behind
       it (the scheduler's rule for idle work, IDLE_INPUT_QUIET_MS). */
    const LOBBY_WARM_INPUT_QUIET_MS = 400;
    let lastInputAt = 0;
    let inputWatched = false;

    function watchInput() {
        if (inputWatched || typeof window === 'undefined') return;
        inputWatched = true;
        const note = () => { lastInputAt = Date.now(); };
        ['pointerdown', 'keydown', 'touchstart', 'wheel'].forEach((type) => {
            window.addEventListener(type, note, { capture: true, passive: true });
        });
    }

    async function quietSlot() {
        for (;;) {
            // eslint-disable-next-line no-await-in-loop
            await idleSlot();
            const quietFor = Date.now() - lastInputAt;
            if (quietFor >= LOBBY_WARM_INPUT_QUIET_MS) return;
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolveWait) => { setTimeout(resolveWait, LOBBY_WARM_INPUT_QUIET_MS - quietFor); });
        }
    }

    /**
     * @param {{ stillIdle?: () => boolean }} [options]
     * @returns {Promise<void>}
     */
    function warmForLobby({ stillIdle = () => true } = {}) {
        let entry;
        try {
            entry = SummonerRoster.read();
        } catch (_) {
            return Promise.resolve();
        }
        if (lobbyWarm && lobbyWarm.roster === entry) return lobbyWarm.promise;
        /** @type {{ roster: object, promise: Promise<void> }} */
        const run = { roster: entry, promise: Promise.resolve() };
        lobbyWarm = run;
        run.promise = entry.promise.then(async (roster) => {
            const record = readFaces();
            seedFromRoster(record, roster);
            if (dialog || !record.faces.length || !samplerOf()) return;
            const fits = firstPageCapacity(record.faces, record.done);
            if (!fits) return;
            const urls = record.faces.slice(0, fits * LOBBY_WARM_PAGES).map((face) => face.imageUrl);
            watchInput();
            for (const url of urls) {
                if (tileSampled.has(url)) continue;
                await quietSlot();
                if (lobbyWarm !== run || dialog || facesRead !== record || !stillIdle()) return;
                await sampleFace(url, { low: true });
            }
        }).catch(() => {
            if (lobbyWarm === run) lobbyWarm = null;
        });
        return run.promise;
    }

    function sameFaces(left, right) {
        return left.length === right.length && left.every((item, index) => (
            item.characterId === right[index].characterId
            && item.name === right[index].name
            && item.imageUrl === right[index].imageUrl
        ));
    }

    /* ONE TOAST, KEPT (round 17, 28 September 2026). A second choice while
       the first one's toast was still up appended a second toast over it and
       pulled the first away 3 s after it came: the meter read a rebuild blink
       (the profile journey puts the account's face back right after changing
       it, and the picker now reopens from memory at once). The toast on screen
       takes the new word in place, and its time starts again. One already
       leaving is not called back for the same words: taken back mid-fade it
       dimmed and came back with nothing changed (an opacity blink), and the
       disc already shows the new face. And one up when the picker opens
       waits for the dialog (holdToast): a choice made in it takes that
       toast, instead of finding it gone a moment before and raising a
       second one in its place (the meter's WebKit phone, 3.4 s between the
       two choices), and its time starts when the dialog closes. */
    /** @type {{ el: HTMLElement, exit: ReturnType<typeof setTimeout> | null, remove: ReturnType<typeof setTimeout> | null, held: boolean } | null} */
    let toastShown = null;

    function armToast(shown) {
        clearTimeout(shown.exit);
        clearTimeout(shown.remove);
        shown.exit = setTimeout(() => shown.el.classList.add('lobby-toast-exit'), 2500);
        shown.remove = setTimeout(() => {
            shown.el.remove();
            if (toastShown === shown) toastShown = null;
        }, 3000);
    }

    function toast(message) {
        if (
            toastShown?.el.isConnected
            && toastShown.el.classList.contains('lobby-toast-exit')
            && toastShown.el.textContent === message
        ) return;
        if (toastShown?.el.isConnected) {
            toastShown.el.classList.remove('lobby-toast-exit');
            if (toastShown.el.textContent !== message) toastShown.el.textContent = message;
        } else {
            const el = document.createElement('div');
            el.className = 'lobby-toast';
            el.setAttribute('role', 'status');
            el.textContent = message;
            document.body.appendChild(el);
            toastShown = { el, exit: null, remove: null, held: false };
        }
        // A toast the open dialog holds starts its time when the dialog closes.
        if (!toastShown.held) armToast(toastShown);
    }

    /* The dialog opened over a toast that is up and not yet leaving: it stays. */
    function holdToast() {
        const shown = toastShown;
        if (!shown?.el.isConnected || shown.el.classList.contains('lobby-toast-exit')) return;
        clearTimeout(shown.exit);
        clearTimeout(shown.remove);
        shown.exit = null;
        shown.remove = null;
        shown.held = true;
    }

    /* The dialog closed: a toast it held leaves on its own time from now. */
    function releaseToast() {
        const shown = toastShown;
        if (!shown?.held) return;
        shown.held = false;
        if (shown.el.isConnected) armToast(shown);
    }

    /* What the open dialog bound outside itself (viewport listeners, its
       hold, its downloads, the pressed disc): closing runs them, so a dialog
       that is gone leaves nothing listening and nothing loading. */
    /** @type {Array<() => void>} */
    let cleanups = [];
    /* FOCUS GIVEN BACK IS NOT INTENT (review of wave D, 28 September 2026).
       Focus on the disc warms the first page (mount), and close() gives the
       focus back to the disc right after it has dropped the dialog's
       downloads: where a press does not focus the button it hit (WebKit,
       Safari, iOS), every close during the hold started those very downloads
       again — for a dialog the player had just dismissed. */
    let givingFocusBack = false;
    /* A CHOICE ON ITS WAY HOLDS THE PAGE (review of wave E, 28 September
       2026). choose() disabled the faces on screen and marked the one pressed
       busy, but a page turn on its way went on: once its faces were painted
       it swapped in a page of ENABLED tiles while setAvatar was still on its
       way — the busy face disappeared, and a second choice could be sent.
       While a choice is on its way the page on screen stays: the choice ends
       any turn (the page it was heading to is set aside, as Prev back does),
       the pager takes no press, and no second choice is sent. A choice that
       fails gives the page back whole. */
    let choosing = false;
    /** @type {{ hold: () => void, release: () => void } | null} the open dialog's page, held for a choice */
    let pageForChoice = null;

    function close({ restoreFocus = true } = {}) {
        if (!dialog) return;
        const node = dialog;
        dialog = null;
        openGeneration += 1;
        node.remove();
        releaseToast();
        document.removeEventListener('keydown', onKeydown, true);
        cleanups.forEach((run) => { try { run(); } catch (_) { /* a listener already gone */ } });
        cleanups = [];
        if (restoreFocus && button?.isConnected) {
            givingFocusBack = true;
            try {
                button.focus({ preventScroll: true });
            } finally {
                givingFocusBack = false;
            }
        }
    }

    function onKeydown(event) {
        if (!dialog) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (event.key !== 'Tab') return;
        const nodes = Array.from(dialog.querySelectorAll('button:not([disabled])'));
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !dialog.contains(active))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
            event.preventDefault();
            first.focus();
        }
    }

    async function choose(characterId, option) {
        if (!dialog || choosing) return;
        choosing = true;
        const options = Array.from(dialog.querySelectorAll('.avatar-picker-option'));
        options.forEach((node) => { node.disabled = true; });
        option?.classList.add('is-busy');
        pageForChoice?.hold();
        try {
            const result = await MoonfallCloud?.users?.setAvatar?.(characterId);
            paint({
                avatarCharacterId: result?.avatarCharacterId ?? null,
                avatarPortraitUrl: result?.avatarPortraitUrl ?? null,
                avatarIsDefault: Boolean(result?.avatarIsDefault),
            });
            toast(uiText('account.avatarSaved', 'Avatar updated'));
            close();
        } catch (error) {
            // Every face on the page, with any a resize added meanwhile.
            const onPage = dialog ? Array.from(dialog.querySelectorAll('.avatar-picker-option')) : [];
            new Set([...options, ...onPage]).forEach((node) => { node.disabled = false; });
            option?.classList.remove('is-busy');
            const code = error?.data?.code;
            toast(code === 'avatar_not_owned'
                ? uiText('account.avatarNotOwned', 'That summoner is not yours to show')
                : uiText('account.avatarFailed', 'Could not update the avatar'));
        } finally {
            choosing = false;
            pageForChoice?.release();
        }
    }

    /** A face's tile: its disc (the picture is painted by the page that
        shows it, never here) and its name. */
    function buildOption({ characterId, name, imageUrl }) {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'avatar-picker-option';
        option.dataset.characterId = characterId;
        if (characterId === currentCharacterId) {
            option.classList.add('is-current');
            option.setAttribute('aria-pressed', 'true');
        } else {
            option.setAttribute('aria-pressed', 'false');
        }
        const disc = document.createElement('span');
        disc.className = 'avatar-picker-disc';
        const img = document.createElement('img');
        img.className = 'avatar-picker-image';
        img.alt = '';
        img.decoding = 'async';
        disc.appendChild(img);
        tileUrls.set(option, imageUrl);
        const label = document.createElement('span');
        label.className = 'avatar-picker-name';
        const text = name || characterId;
        /* A NAME IS NEVER CUT. Summoners are named by the content models, so
           they run from "Kitsu" to "Nachtwandlerin der Mondfinsternis"; a
           long one takes a smaller size rather than an ellipsis, and the
           tile (and with it the number of faces on the page) grows to hold
           it. The thresholds are character counts rather than a measure
           because the answer only has to be "smaller", not exact. */
        if (text.length > 30) label.classList.add('is-very-long');
        else if (text.length > 18) label.classList.add('is-long');
        label.textContent = text;
        option.append(disc, label);
        option.addEventListener('click', () => choose(characterId, option));
        return option;
    }

    async function open() {
        if (!resolve()) return false;
        if (dialog) { close(); return false; }
        const generation = ++openGeneration;
        const {
            overlay, grid, pager, prevButton, pageLabel, nextButton, closeButton,
        } = buildShell();
        closeButton.addEventListener('click', () => close());

        /* THE DIALOG IS PREPARED UNSEEN. Its backdrop is up at once — the
           press's answer, and a tap on it closes the dialog — while the card
           is laid out behind the hold (hub.css) until the page is whole. A
           wait past the grace adds the ONE loading state (a spinner on the
           backdrop, marked `data-loading`). */
        const releasePress = typeof TouchPress?.holdPending === 'function' ? TouchPress.holdPending(button) : () => {};
        /** @type {Array<ReturnType<typeof createFaceSession>>} */
        const sessions = [];
        const newSession = (options) => {
            const session = createFaceSession(options);
            sessions.push(session);
            return session;
        };
        let presented = false;
        /* WHAT IS LEFT AT THE GRACE DECIDES (the Collection's rule,
           docs/asset-loading.md). With the read, a portrait's bytes or the
           names' type still on the wire, the ONE loading state shows; with
           only the faces' reduction left, the dialog finishes unseen within
           its cap — a spinner there only flashed in front of the dialog (4 ms
           on the meter's warm desktop open, wd-avatar-1). So the hold shows
           no state of its own, and the grace below asks for it. */
        const holdOptions = (delay) => ({
            label: () => uiText('common.pleaseWait', 'Please wait…'),
            loadingDelayMs: delay,
            onRelease: (reason) => {
                if (reason === 'superseded') return;
                releasePress();
                if (reason === 'close') return;
                presented = true;
                // Shown: nothing more waits for the hold.
                holdPaints = null;
                onPresented();
            },
        });
        // The longest a timer waits: no loading state until the grace asks.
        const NO_LOADING_STATE_OF_ITS_OWN = 2147483647;
        let hold = SurfaceEntryHold.hold(overlay, holdOptions(NO_LOADING_STATE_OF_ITS_OWN));
        /** @type {number | null} when the hold's cap falls, once armed */
        let capAt = null;
        let readsOnTheWire = false;
        let fontsOnTheWire = false;
        const graceTimer = setTimeout(() => {
            if (!hold.active || !isCurrent()) return;
            if (!readsOnTheWire && !fontsOnTheWire && !pageSession.onTheWire()) return;
            // The same hold, now with its one loading state (shown at once).
            hold.release('superseded');
            hold = SurfaceEntryHold.hold(overlay, holdOptions(0));
            if (capAt !== null) hold.armCap(Math.max(0, capAt - Date.now()));
        }, LOADING_GRACE_MS);
        overlay.addEventListener('pointerdown', (event) => {
            if (event.target === overlay) close();
        });
        document.body.appendChild(overlay);
        dialog = overlay;
        holdToast();
        document.addEventListener('keydown', onKeydown, true);
        cleanups.push(
            () => clearTimeout(graceTimer),
            () => hold.release('close'),
            () => sessions.forEach((session) => session.close()),
            releasePress,
        );

        const isCurrent = () => generation === openGeneration && dialog === overlay;

        /* Every face read, in the route's order: the pages are windows onto
           it, so turning back costs nothing and turning forward reads only
           what is missing. Kept across opens (facesRead). */
        let read = readFaces();
        const warmOpen = read.faces.length > 0 || read.done;
        let pageIndex = 0;
        /* THE FIRST FACE OF EACH PAGE. A page holds however many tiles FIT the
           dialog at this size, which is not a constant — it changes with the
           viewport, and with the two-line names — so the pages are recorded by
           where they start rather than computed from a page size. */
        const pageStarts = [0];
        /* The pages seen this open, by their first face: their tiles, painted,
           off the page. Turning back shows them as they were. */
        /** @type {Map<number, HTMLElement[]>} */
        const keptPages = new Map();
        /** @type {{ index: number, start: number, tiles: HTMLElement[], done: Promise<void>, session: ReturnType<typeof createFaceSession> } | null} */
        let prepared = null;
        let turning = false;
        /* A re-read that changed the faces during a turn: page one is shown
           again, whole, once that turn is over. */
        let freshAfterTurn = false;
        /** @type {number | null} */
        let capacity = null;
        let pageSession = newSession();
        /* THE HOLD WAITS FOR EVERY FACE IT WILL SHOW (review of wave E, 28
           September 2026). It was released once the tiles it started
           painting were painted, and a resize while it waited laid the page
           out again with the faces that now fit, painted outside that
           promise: they could be shown with their pictures still loading, in
           the dialog's first frame. Every paint of a tile added while the
           dialog is held joins the hold's wait (everyHoldPaint); the cap
           still bounds it. Null once the dialog is shown. */
        /** @type {Array<Promise<void>> | null} */
        let holdPaints = [];
        /** @type {ReturnType<typeof setTimeout> | null} the running turn's dim, past the grace */
        let turnStaleTimer = null;

        const currentTiles = () => /** @type {HTMLElement[]} */ (
            Array.from(grid.querySelectorAll(':scope > .avatar-picker-option'))
        );

        function showStatus(text) {
            grid.replaceChildren();
            grid.removeAttribute('aria-busy');
            const status = document.createElement('p');
            status.className = 'avatar-picker-status';
            status.textContent = text;
            grid.appendChild(status);
        }

        /**
         * Read until `count` faces are in hand or the route is spent. The
         * route's pages are ROWS, not faces: a row with no portrait is not one
         * (the server refuses it), so a read can yield fewer faces than rows —
         * or none at all, which is why the loop ends on the route's own word
         * rather than on arithmetic. Reads run one after another, so a turn
         * and the page fetched ahead never read the same rows twice.
         * @param {number} count
         * @returns {Promise<boolean>} false when this open is no longer current
         */
        function readUntil(count) {
            const record = read;
            const run = (record.reading || Promise.resolve()).then(async () => {
                // A warm read already on its way is the first page; wait for it
                // rather than asking the route for the same rows twice.
                if (record.pending) await record.pending;
                // The first route page is the shared roster: read once for
                // this dialog and the summoner select.
                if (!record.faces.length && !record.done && !record.cursor) {
                    if (!isCurrent() || record !== read) return false;
                    await readFirstPage(record);
                    if (facesRead !== record) return false;
                }
                while (record.faces.length < count && !record.done) {
                    if (!isCurrent() || record !== read) return false;
                    const page = await fetchSummoners(record.cursor);
                    if (facesRead !== record) return false;
                    record.faces.push(...page.items);
                    record.cursor = page.nextCursor;
                    record.done = page.isDone || !page.nextCursor;
                }
                return true;
            });
            record.reading = run.then(() => undefined, () => undefined);
            return run.then((ok) => ok && isCurrent() && record === read);
        }

        /** Drop tiles past what fits, and answer how many are left. */
        function trimToFit(count) {
            const fits = fitCountOf(grid);
            if (fits === null) return count;
            capacity = fits;
            if (fits >= count) return count;
            currentTiles().slice(fits).forEach((tile) => tile.remove());
            return fits;
        }

        /* ONE PAGE OF FACES, AND ONE MORE. A page holds what fits (measured
           once laid out), and one face past a full page is what proves another
           page exists; a screen that fits more than one route page asks for
           more. Before the first layout, the room is not known. */
        function pageWant(start) {
            return start + Math.min(MAX_PAGE, capacity ?? MAX_PAGE) + 1;
        }

        function paintPager(index, start, shown) {
            const hasNext = read.faces.length > start + shown || (!read.done && shown > 0);
            pageStarts[index + 1] = start + shown;
            pager.hidden = index === 0 && !hasNext;
            prevButton.disabled = index === 0;
            nextButton.disabled = !hasNext;
            pageLabel.textContent = uiText('common.page', 'Page {{page}}', { page: index + 1 });
        }

        /* THE PAGE'S BOX IS KEPT. A shorter last page (or one of one-line
           names) would shrink the card and move every control in it; the
           first page's height holds while the dialog pages. */
        function keepPageBox() {
            grid.style.minHeight = '';
            if (pager.hidden) return;
            const height = grid.offsetHeight;
            if (height > 0) grid.style.minHeight = `${height}px`;
        }

        /**
         * Lay one page out in the grid, pictures not started: fill it with
         * every face it could hold, measure what fits, drop the overflow (so
         * the dialog never scrolls), show the pager if another page exists
         * and measure again in the room it leaves. One task: nothing paints
         * between the states.
         * @returns {number} the faces on the page
         */
        function layoutPage(index) {
            pageIndex = index;
            const start = pageStarts[index] ?? 0;
            grid.style.minHeight = '';
            if (!read.faces.length || start >= read.faces.length) {
                showStatus(uiText('account.avatarEmpty', 'No summoners with a portrait yet.'));
                paintPager(index, start, 0);
                return 0;
            }
            const available = Math.min(MAX_PAGE, read.faces.length - start);
            grid.replaceChildren(...read.faces.slice(start, start + available).map((summoner) => buildOption(summoner)));
            grid.removeAttribute('aria-busy');
            fitNamesIn(grid);
            // Measured first without the pager: a page that fits whole needs none.
            pager.hidden = true;
            let shown = trimToFit(available);
            const paginated = index > 0 || read.faces.length > start + shown || (!read.done && shown > 0);
            pager.hidden = !paginated;
            if (paginated) shown = trimToFit(shown);
            paintPager(index, start, shown);
            keepPageBox();
            if (capacity !== null && index === 0) pageCapacity.set(viewKey(), capacity);
            return shown;
        }

        function focusPage() {
            const target = /** @type {HTMLButtonElement | null} */ (
                grid.querySelector('.avatar-picker-option.is-current')
                || grid.querySelector('.avatar-picker-option')
                || closeButton
            );
            target?.focus({ preventScroll: true });
        }

        /**
         * Show `count` faces from `start` by adjusting the tiles on screen:
         * the ones already there stay (their pictures are painted), the tail
         * past the count is removed, and only missing faces are built.
         * Rebuilding every tile blanked or re-sharpened each face per frame
         * of a drag-resize.
         */
        function reconcileTiles(start, count) {
            const tiles = currentTiles();
            const wanted = read.faces.slice(start, start + count);
            const kept = tiles
                .slice(0, Math.min(tiles.length, wanted.length))
                .every((tile, index) => tile.dataset.characterId === wanted[index].characterId);
            if (!kept) {
                grid.replaceChildren();
                return reconcileTiles(start, count);
            }
            tiles.slice(wanted.length).forEach((tile) => tile.remove());
            const added = wanted.slice(tiles.length).map((summoner) => buildOption(summoner));
            if (added.length) {
                // Added while a choice is on its way: as the faces beside them.
                if (choosing) added.forEach((tile) => { tile.disabled = true; });
                grid.append(...added);
                fitNamesIn(grid);
                const painting = paintTiles(added, pageSession);
                holdPaints?.push(painting);
            }
            return wanted.length;
        }

        /* One more measurement once the page is on screen, for type that
           landed after the hold's cap: if fewer tiles fit than are shown, the
           tail goes. Bounded — it never grows the page, so it cannot
           oscillate. */
        function refitShownPage() {
            if (!isCurrent()) return;
            const shown = currentTiles().length;
            if (!shown) return;
            const fits = fitCountOf(grid);
            if (fits === null || fits >= shown) return;
            const start = pageStarts[pageIndex] ?? 0;
            paintPager(pageIndex, start, reconcileTiles(start, fits));
            keptPages.clear();
            dropPrepared();
        }

        /** A page's tiles, built off the page and painted unseen. */
        function preparePage(index, start, { low = false } = {}) {
            const room = capacity ?? (currentTiles().length || MAX_PAGE);
            const count = Math.max(1, Math.min(MAX_PAGE, room, read.faces.length - start));
            const session = newSession({ low });
            const tiles = read.faces.slice(start, start + count).map((summoner) => buildOption(summoner));
            return { index, start, tiles, session, done: paintTiles(tiles, session) };
        }

        function dropPrepared() {
            if (!prepared) return;
            prepared.session.close();
            prepared = null;
        }

        /** The page to show at `start`: kept from before, fetched ahead, or null. */
        function takeReadyPage(index, start) {
            const kept = keptPages.get(start);
            if (kept) {
                keptPages.delete(start);
                const done = Promise.all(kept.map((tile) => {
                    const img = faceImage(tile);
                    return img && img.getAttribute('src') ? decoded(img) : undefined;
                })).then(() => undefined);
                return { index, start, tiles: kept, session: pageSession, done };
            }
            if (prepared && prepared.start === start) {
                const page = prepared;
                prepared = null;
                return page;
            }
            return null;
        }

        /** Put a whole page in place of the one on screen, in one task. */
        function swapIn(page, { focus }) {
            grid.classList.remove('avatar-picker-grid--stale');
            const leaving = currentTiles();
            const leavingStart = pageStarts[pageIndex];
            if (leaving.length && leavingStart !== undefined && leavingStart !== page.start) {
                keptPages.set(leavingStart, leaving);
            }
            grid.replaceChildren(...page.tiles);
            pageIndex = page.index;
            pageSession = page.session;
            fitNamesIn(grid);
            const shown = trimToFit(page.tiles.length);
            paintPager(page.index, page.start, shown);
            // More than one page ahead of the page now shown: dropped.
            if (prepared && prepared.index !== page.index + 1) dropPrepared();
            if (focus) focusPage();
        }

        /* ONE PAGE AHEAD, once the page on screen has its faces: the next
           page's tiles are painted off the page, at low priority, so Next
           finds them whole. Dropped when the player turns away or closes. */
        function fetchAhead() {
            if (!isCurrent() || !presented || turning || nextButton.disabled) return;
            const index = pageIndex + 1;
            const start = pageStarts[index];
            if (start === undefined || keptPages.has(start) || prepared?.start === start) return;
            if (read.faces.length <= start) {
                if (read.done) return;
                void readUntil(pageWant(start)).then((ok) => {
                    if (ok && read.faces.length > start) fetchAhead();
                }, () => {});
                return;
            }
            dropPrepared();
            prepared = preparePage(index, start, { low: true });
        }

        /** Once the page on screen has its faces, the next page's. */
        function aheadOnceSettled(done, index) {
            done.then(() => {
                if (isCurrent() && pageIndex === index) fetchAhead();
            });
        }

        /* FAST HANDS (review of wave D, 28 September 2026). A turn waits, up
           to PAGE_TURN_CAP_MS, for the next page's faces, and a Next or Prev
           pressed meanwhile used to be dropped (the turn refused any press
           while it ran): nothing answered it and the player ended a page
           short — where before the hold a page with its faces in hand turned
           at once. A press during a turn now moves where it is going
           (`turnGoal`), from the page it is heading to, like the Collection's,
           the Deck Builder's and the summoner select's pagers: the page asked
           for last is the one painted, a page passed over is laid out unseen
           only to find where the next one starts (never painted, none of its
           pictures fetched), and Prev back to the page on screen ends the
           turn. */
        /** @type {number | null} the page index the running turn is heading to */
        let turnGoal = null;
        /** @type {(() => void) | null} ends the running turn's wait when a press moves it */
        let turnMoved = null;

        function pressPager(delta) {
            if (!isCurrent() || choosing) return;
            if (!turning) {
                void turnTo(pageIndex + delta);
                return;
            }
            const goal = (turnGoal ?? pageIndex) + delta;
            if (goal < 0 || goal === turnGoal) return;
            turnGoal = goal;
            turnMoved?.();
        }

        /**
         * How many faces the page starting at `start` holds: the real dialog
         * laid out unseen with that page's faces (the pager shown, as on every
         * page past the first) and removed in the same task, so a page passed
         * over is never painted and none of its pictures is fetched. Where
         * there is no layout to read, the room a page is built with
         * (`preparePage`).
         */
        function measurePageCount(start) {
            const available = Math.min(MAX_PAGE, read.faces.length - start);
            if (available <= 0) return 0;
            let fits = null;
            if (document.body) {
                const shell = buildShell({ probe: true });
                document.body.appendChild(shell.overlay);
                try {
                    shell.pager.hidden = false;
                    shell.grid.replaceChildren(...read.faces.slice(start, start + available).map((summoner) => buildOption(summoner)));
                    fitNamesIn(shell.grid);
                    fits = fitCountOf(shell.grid);
                } finally {
                    shell.overlay.remove();
                }
            }
            const room = fits ?? capacity ?? (currentTiles().length || MAX_PAGE);
            return Math.max(1, Math.min(available, room));
        }

        /**
         * Where page `index` starts: read as far as it needs, and the pages
         * before it that were never shown measured for their count. A page
         * past the end answers the last page instead. Null once this open is
         * no longer current (or the faces were read again meanwhile).
         * @returns {Promise<{ index: number, start: number } | null>}
         */
        async function pageOnTheWay(index) {
            const record = read;
            let known = Math.max(0, index);
            while (known > 0 && pageStarts[known] === undefined) known -= 1;
            for (;;) {
                const start = pageStarts[known] ?? 0;
                if (read.faces.length < pageWant(start) && !read.done) {
                    try {
                        if (!await readUntil(pageWant(start))) return null;
                    } catch (error) {
                        if (!isCurrent() || record !== read) return null;
                        // Nothing to show at all: the caller says so.
                        if (!read.faces.length) throw error;
                        // Otherwise the faces in hand are what there is.
                    }
                    if (!isCurrent() || record !== read) return null;
                }
                if (known === index) return { index, start };
                const nextStart = start + measurePageCount(start);
                // No page after this one: the turn stops here.
                if (nextStart >= read.faces.length) return { index: known, start };
                pageStarts[known + 1] = nextStart;
                known += 1;
            }
        }

        /** A page a turn built or took and no longer shows: kept where it belongs. */
        function setAside(page) {
            if (page.session === pageSession) {
                // A page kept from before (its tiles painted): kept again.
                keptPages.set(page.start, page.tiles);
            } else if (page.index === pageIndex + 1 && !prepared) {
                // One page ahead of the page on screen: kept, its faces coming.
                prepared = page;
            } else {
                page.session.close();
            }
        }

        /**
         * Turn to page `index`. The page on screen stays — whole, marked busy,
         * dimmed past the grace — until the page the turn is heading to has
         * its faces painted (at most PAGE_TURN_CAP_MS from when it was asked
         * for), then that page replaces it in one step. A press meanwhile
         * moves where the turn is heading (pressPager).
         */
        async function turnTo(index, { focus = true, fresh = false } = {}) {
            if (turning || index < 0 || !isCurrent() || choosing) return;
            if (pageStarts[index] === undefined) return;
            turning = true;
            turnGoal = index;
            grid.setAttribute('aria-busy', 'true');
            turnStaleTimer = setTimeout(() => {
                if (isCurrent()) grid.classList.add('avatar-picker-grid--stale');
            }, LOADING_GRACE_MS);
            try {
                for (;;) {
                    if (!isCurrent()) return;
                    // A choice on its way holds the page on screen: the turn
                    // ends (a re-read's turn runs if the choice fails).
                    if (choosing) {
                        if (fresh) freshAfterTurn = true;
                        return;
                    }
                    const goal = /** @type {number} */ (turnGoal);
                    // Taken back to the page on screen: nothing is painted.
                    if (goal === pageIndex && !fresh) return;
                    let target;
                    try {
                        target = await pageOnTheWay(goal);
                    } catch (_) {
                        if (isCurrent()) showStatus(uiText('account.avatarFailed', 'Could not update the avatar'));
                        return;
                    }
                    if (!target || !isCurrent()) return;
                    // Moved while its way was read (a press, or a choice): head for the new goal.
                    if (turnGoal !== goal || choosing) continue;
                    // Past the end: the last page is where it stops.
                    turnGoal = target.index;
                    if (target.index === pageIndex && !fresh) return;
                    if (target.index > 0 && read.faces.length <= target.start) return;
                    const page = (!fresh && takeReadyPage(target.index, target.start))
                        || preparePage(target.index, target.start);
                    const moved = new Promise((resolveMove) => { turnMoved = () => resolveMove(undefined); });
                    await Promise.race([bounded(page.done, PAGE_TURN_CAP_MS), moved]);
                    turnMoved = null;
                    if (!isCurrent()) {
                        page.session.close();
                        return;
                    }
                    if (turnGoal !== target.index || choosing) {
                        setAside(page);
                        continue;
                    }
                    swapIn(page, { focus });
                    aheadOnceSettled(page.done, page.index);
                    return;
                }
            } finally {
                endTurnLook();
                turning = false;
                turnGoal = null;
                turnMoved = null;
                if (freshAfterTurn && isCurrent() && !choosing) {
                    freshAfterTurn = false;
                    void turnTo(0, { focus: false, fresh: true });
                }
            }
        }

        /** The running turn's busy state and its dim go. */
        function endTurnLook() {
            clearTimeout(turnStaleTimer);
            turnStaleTimer = null;
            grid.classList.remove('avatar-picker-grid--stale');
            grid.removeAttribute('aria-busy');
        }

        /* A choice on its way: a turn running is taken back to the page on
           screen at once (its page set aside, as Prev back does), its busy
           state and dim gone; nothing turns until the choice is answered. A
           choice that failed runs the re-read's turn it held back. */
        pageForChoice = {
            hold() {
                if (!isCurrent() || !turning) return;
                turnGoal = pageIndex;
                turnMoved?.();
                endTurnLook();
            },
            release() {
                if (!isCurrent() || turning || !freshAfterTurn) return;
                freshAfterTurn = false;
                void turnTo(0, { focus: false, fresh: true });
            },
        };
        cleanups.push(() => { pageForChoice = null; });

        /* A resize changes how many faces fit, so the page is laid out again
           from the same first face — the player keeps their place, the grid
           keeps its promise not to scroll. */
        let resizeFrame = 0;
        const onViewportChange = () => {
            if (!isCurrent()) return;
            if (resizeFrame) cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(() => {
                resizeFrame = 0;
                if (!isCurrent() || !grid.querySelector('.avatar-picker-option')) return;
                // Pages measured at the old size are measured again when shown.
                keptPages.clear();
                dropPrepared();
                grid.style.minHeight = '';
                const start = pageStarts[pageIndex] ?? 0;
                const available = Math.min(MAX_PAGE, read.faces.length - start);
                const fits = fitCountOf(grid);
                if (fits !== null) capacity = fits;
                /* Nothing to measure (a grid momentarily 0 tall): show what the
                   page has, as a first paint does — never record more faces as
                   shown than are on screen, or Next skips the ones between. */
                const shown = fits === null
                    ? reconcileTiles(start, available)
                    : reconcileTiles(start, Math.min(available, Math.max(1, fits)));
                // A tile appended above has only now been measured.
                const settledFits = fitCountOf(grid);
                const final = settledFits !== null && settledFits < shown
                    ? reconcileTiles(start, settledFits)
                    : shown;
                paintPager(pageIndex, start, final);
                keepPageBox();
            });
        };
        window.addEventListener('resize', onViewportChange);
        window.addEventListener('orientationchange', onViewportChange);
        cleanups.push(() => {
            window.removeEventListener('resize', onViewportChange);
            window.removeEventListener('orientationchange', onViewportChange);
            if (resizeFrame) cancelAnimationFrame(resizeFrame);
        });

        prevButton.addEventListener('click', () => pressPager(-1));
        nextButton.addEventListener('click', () => pressPager(1));

        /* The first route page again, through the shared roster
           (summoner-roster.js): a list that changed is changed for the
           summoner select too. */
        async function readRosterAgain() {
            if (SummonerRoster.peek()) {
                await SummonerRoster.revalidate({ force: true });
                const roster = SummonerRoster.peek();
                if (roster) return roster;
            }
            return SummonerRoster.read().promise;
        }

        /* The faces painted from memory are re-read behind the open: an
           unchanged first route page changes nothing; a changed one starts
           the record again and turns to its first page, whole. */
        async function revalidateFaces() {
            const record = read;
            let page;
            try {
                const roster = await readRosterAgain();
                page = {
                    items: facesOf(roster?.rows),
                    nextCursor: roster?.nextCursor || null,
                    isDone: Boolean(roster?.isDone) || !roster?.nextCursor,
                };
            } catch (_) {
                return;
            }
            if (!isCurrent() || record !== read) return;
            if (sameFaces(page.items, record.faces.slice(0, page.items.length))
                && (page.isDone || !page.nextCursor) === (record.done && record.faces.length === page.items.length)) {
                return;
            }
            facesRead = { key: record.key, faces: [...page.items], cursor: page.nextCursor, done: page.isDone || !page.nextCursor };
            read = facesRead;
            pageStarts.length = 1;
            keptPages.clear();
            dropPrepared();
            if (turning || choosing) freshAfterTurn = true;
            else await turnTo(0, { focus: false, fresh: true });
        }

        function onPresented() {
            focusPage();
            const fonts = namesFontsPending();
            if (fonts) fonts.then(() => refitShownPage());
            if (warmOpen) void revalidateFaces();
        }

        let shown = 0;
        if (warmOpen) {
            shown = layoutPage(0);
        } else {
            /* A cold open reads what its page needs: the room this viewport
               was measured to hold (a warm on intent measured it), or one
               route read; then, laid out unseen, the rest of the page if it
               is not full, and one face past it. */
            capacity = pageCapacity.get(viewKey()) ?? null;
            readsOnTheWire = true;
            try {
                if (!await readUntil(capacity === null ? 1 : pageWant(0))) return true;
                shown = layoutPage(0);
                for (let guard = 0; guard < 4 && !read.done && read.faces.length < pageWant(0); guard += 1) {
                    if (!await readUntil(pageWant(0))) return true;
                    shown = layoutPage(0);
                }
            } catch (_) {
                if (!isCurrent()) return true;
                showStatus(uiText('account.avatarFailed', 'Could not update the avatar'));
                hold.release();
                return true;
            } finally {
                readsOnTheWire = false;
            }
        }
        if (!isCurrent()) return true;
        // Capped from the moment the reads answered.
        hold.armCap(OPEN_REVEAL_CAP_MS);
        capAt = Date.now() + OPEN_REVEAL_CAP_MS;
        if (!shown) {
            hold.release();
            return true;
        }
        const fonts = namesFontsPending();
        if (fonts) {
            fontsOnTheWire = true;
            await bounded(fonts, capAt - Date.now());
            fontsOnTheWire = false;
            if (!isCurrent()) return true;
            // Measured again in the real type, still unseen.
            if (!presented) layoutPage(0);
        }
        /** Every paint the hold waits for, the ones added while it waited included. */
        async function everyHoldPaint() {
            for (let seen = 0; holdPaints && seen < holdPaints.length;) {
                const batch = holdPaints.slice(seen);
                seen = holdPaints.length;
                // eslint-disable-next-line no-await-in-loop
                await Promise.all(batch);
            }
        }
        holdPaints?.push(paintTiles(currentTiles(), pageSession));
        void everyHoldPaint().then(() => {
            holdPaints = null;
            if (!isCurrent()) return;
            // Presented now, unless the cap already did.
            hold.release('ready');
            if (pageIndex === 0) fetchAhead();
        });
        return true;
    }

    return {
        mount,
        paint,
        open,
        close,
        isOpen: () => dialog !== null,
        /** The disc's final face is on screen (never rejects). */
        whenPainted: () => painted,
        warm,
        /** The lobby's idle warm of the faces (lobby.js). */
        warmForLobby,
        /** Sign-out: this browser no longer starts the account's face. */
        forget: forgetFace,
    };
})();

export { AvatarPicker };
